<template>
    <Loading v-if="loading"/>
    <Page>
        <Container>
            <div class="mx-auto max-w-sm">

                <Title>{{ emailFirstSignup ? 'Starting Your Free Trial' : 'Sign Up' }}</Title>

                <template v-if="emailFirstSignup">
                    <Paragraph class="mb-5">
                        Creating your V3ME account for {{ form.fields.email }}.
                    </Paragraph>

                    <div v-if="turnstileSiteKey" ref="turnstileContainer" class="mb-3 min-h-[65px]"/>

                    <Button v-if="!turnstileSiteKey" id="emailFirstSignUpButton" primary block @click="handleEmailFirstSignUp" class="mb-3">
                        Start My Free Trial
                    </Button>

                    <Help class="text-center">Free for 7 days. No card required.</Help>
                </template>

                <template v-else>

                <Field>
                    <Label>First Name *</Label>
                    <TextInput id="firstName" v-model="form.fields.firstName" placeholder="First Name"/>
                    <FormError :form="form" field="firstName"/>
                </Field>

                <Field>
                    <Label>Last Name *</Label>
                    <TextInput id="lastName" v-model="form.fields.lastName" placeholder="Last Name"/>
                    <FormError :form="form" field="lastName"/>
                </Field>

                <Field>
                    <Label>Email *</Label>
                    <TextInput id="email" v-model="form.fields.email" placeholder="Email"/>
                    <FormError :form="form" field="email"/>
                </Field>

                <Field>
                    <Label>Password *</Label>
                    <PasswordInput id="password" v-model="form.fields.password"/>
                    <FormError :form="form" field="password"/>
                </Field>

                <Field>
                    <Label>Password Confirmation *</Label>
                    <PasswordInput id="passwordConfirmation" v-model="form.fields.passwordConfirmation"/>
                    <FormError :form="form" field="passwordConfirmation"/>
                </Field>

                <div v-if="turnstileSiteKey" ref="turnstileContainer" class="mb-3 min-h-[65px]"/>

                <Button id="signUpButton" primary block @click="handleSignUp" class="mb-3">
                    Start My Free Trial
                </Button>

                <Help class="text-center">No Payment Method Required</Help>

                <div class="text-center my-5">
                    - or -
                </div>

                <Button secondary outlined block @click="handleLogIn">
                    Log In
                </Button>

                </template>

            </div>
        </Container>
    </Page>
</template>

<script>
import {defineComponent} from 'vue'
import {mapActions} from 'vuex';
import {Form} from '@/internal.js';
import {isLocal} from '@/helpers.js';
import {getStoredUtmParams, clearUtmParams} from '@/utils/utmTracking';
import {capturePostHogEvent, identifyPostHogUser} from '@/utils/posthog';
import Paragraph from '@/components/Paragraph.vue';

export default defineComponent({
  name: 'SignUp',
  components: {Paragraph},
  computed: {
    emailFirstSignup() {
      return this.$route.query.email_first === '1' && Boolean(this.$route.query.email);
    },
    user() {
      return this.$store.getters['auth/user'];
    },
  },
  created() {
    if (isLocal()) {
      this.form.fields = {
        firstName: 'Nick',
        lastName: 'Hough',
        email: `nick+${Math.floor(Math.random() * (9999 - 1000 + 1)) + 1000}@portonefive.com`,
        password: 'secret',
        passwordConfirmation: 'secret',
      }
    }

    if (this.$route.query.email) {
      this.form.fields.email = this.$route.query.email;
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initTurnstile();
    });
  },
  beforeUnmount() {
    if (this.turnstileWidgetId !== null && window.turnstile?.remove) {
      try {
        window.turnstile.remove(this.turnstileWidgetId);
      } catch (e) {
        /* ignore */
      }
    }
  },
  data() {
    return {
      form: new Form({
        email: '',
        firstName: '',
        lastName: '',
        password: '',
        passwordConfirmation: '',
      }),
      loading: false,
      turnstileSiteKey: import.meta.env.VITE_CLOUDFLARE_SITE_KEY || '',
      turnstileWidgetId: null,
      turnstileToken: null,
    };
  },
  methods: {
    ...mapActions({
      authSignUp: 'auth/signUp',
      authEmailSignUp: 'auth/emailSignUp',
    }),
    handleLogIn() {
      this.$router.push(window.app.findRouteByName('auth/login'));
    },
    loadTurnstileScript() {
      if (window.turnstile) {
        return Promise.resolve();
      }
      return new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
        s.async = true;
        s.defer = true;
        s.onload = () => resolve();
        s.onerror = () => reject(new Error('Turnstile failed to load'));
        document.head.appendChild(s);
      });
    },
    initTurnstile() {
      if (!this.turnstileSiteKey || !this.$refs.turnstileContainer) {
        return;
      }
      this.loadTurnstileScript().then(() => {
        this.turnstileWidgetId = window.turnstile.render(this.$refs.turnstileContainer, {
          sitekey: this.turnstileSiteKey,
          callback: (token) => {
            this.turnstileToken = token;
            if (this.emailFirstSignup) {
              this.handleEmailFirstSignUp();
            }
          },
          'expired-callback': () => {
            this.turnstileToken = null;
          },
          'error-callback': () => {
            this.turnstileToken = null;
          },
        });
      }).catch(() => {
        window.app.snackbar({
          type: 'error',
          message: 'Security check failed to load. Please refresh the page.',
        });
      });
    },
    handleSignUp() {

      if (this.loading) {
        return;
      }

      this.form.errors.clear();

      if (this.turnstileSiteKey && !this.turnstileToken) {
        window.app.snackbar({
          type: 'error',
          message: 'Please complete the security check before signing up.',
        });
        return;
      }

      this.loading = true;

      // Get stored UTM parameters
      const utmParams = getStoredUtmParams();
      const signUpPayload = {
        ...this.form.fields,
        ...(this.turnstileToken ? { cfTurnstileResponse: this.turnstileToken } : {}),
      };

      // Include UTM parameters if they exist
      if (utmParams) {
        signUpPayload.utm_params = utmParams;
      }

      capturePostHogEvent('user_signup_submitted', {
        has_marketing_attribution: Boolean(utmParams),
        ...(utmParams || {}),
      });

      return this.authSignUp(signUpPayload).then(() => {

        // Track GA4 conversion event
        if (window.gtag && import.meta.env.VITE_APP_ENV !== 'local') {
          window.gtag('event', 'sign_up', {
            event_category: 'engagement',
            event_label: 'user_signup',
            value: 1
          });
        }

        if (window.fbq && import.meta.env.VITE_APP_ENV !== 'local') {
          window.fbq('track', 'CompleteRegistration', {
            content_name: 'V3ME Sign Up',
            status: true,
            ...(utmParams || {}),
          });
        }

        // Clear UTM params after successful sign up
        if (utmParams) {
          clearUtmParams();
        }

        identifyPostHogUser(this.user);
        capturePostHogEvent('user_signed_up', {
          email: this.user.email,
          has_marketing_attribution: Boolean(utmParams),
          ...(utmParams || {}),
        });

        if (this.user.isV2() && !this.user.isV2ImportComplete()) {

          window.app.snackbar('Welcome to V3!');

          this.$router.push(window.app.findRouteByName('my/import'));

        } else {

          window.app.snackbar({
            message: 'Sign Up Successful!',
            type: 'success',
          });

          this.$router.push(window.app.findRouteByName('my/onboard'));
        }

      }).catch((error) => {
        const response = error?.response;
        const errors = response?.data?.errors || {};

        capturePostHogEvent('user_signup_failed', {
          has_marketing_attribution: Boolean(utmParams),
          status: response?.status || null,
          error_keys: Object.keys(errors),
          message: response?.data?.message || null,
          ...(utmParams || {}),
        });

        window.app.snackbarError(error);
        if (response?.data?.errors) {
          this.form.recordErrors(error);
        }
        if (this.turnstileWidgetId !== null && window.turnstile?.reset) {
          window.turnstile.reset(this.turnstileWidgetId);
        }
        this.turnstileToken = null;
      }).finally(() => {
        this.loading = false;
      });
    },
    handleEmailFirstSignUp() {

      if (this.loading) {
        return;
      }

      this.form.errors.clear();

      if (this.turnstileSiteKey && !this.turnstileToken) {
        return;
      }

      this.loading = true;

      const utmParams = getStoredUtmParams();
      const signUpPayload = {
        email: this.form.fields.email,
        ...(this.turnstileToken ? { cfTurnstileResponse: this.turnstileToken } : {}),
      };

      if (utmParams) {
        signUpPayload.utm_params = utmParams;
      }

      capturePostHogEvent('marketing_funnel_email_signup_submitted', {
        has_marketing_attribution: Boolean(utmParams),
        ...(utmParams || {}),
      });

      return this.authEmailSignUp(signUpPayload).then(() => {
        if (window.gtag && import.meta.env.VITE_APP_ENV !== 'local') {
          window.gtag('event', 'sign_up', {
            event_category: 'engagement',
            event_label: 'email_first_signup',
            value: 1,
          });
          window.gtag('event', 'conversion', {
            send_to: 'AW-17356432382/8jDICLGDytUcEP7HmNRA',
            value: 1,
            currency: 'USD',
          });
        }

        if (window.fbq && import.meta.env.VITE_APP_ENV !== 'local') {
          window.fbq('track', 'CompleteRegistration', {
            content_name: 'V3ME Email-First Sign Up',
            status: true,
            ...(utmParams || {}),
          });
        }

        if (utmParams) {
          clearUtmParams();
        }

        identifyPostHogUser(this.user);
        capturePostHogEvent('user_signed_up', {
          email: this.user.email,
          signup_method: 'email_first',
          has_marketing_attribution: Boolean(utmParams),
          ...(utmParams || {}),
        });

        this.$router.push(window.app.findRouteByName('my/onboard'));
      }).catch((error) => {
        const response = error?.response;

        capturePostHogEvent('marketing_funnel_email_signup_failed', {
          has_marketing_attribution: Boolean(utmParams),
          status: response?.status || null,
          error_keys: Object.keys(response?.data?.errors || {}),
          ...(utmParams || {}),
        });

        window.app.snackbarError(error);
        if (response?.data?.errors) {
          this.form.recordErrors(error);
        }
        if (this.turnstileWidgetId !== null && window.turnstile?.reset) {
          window.turnstile.reset(this.turnstileWidgetId);
        }
        this.turnstileToken = null;
      }).finally(() => {
        this.loading = false;
      });
    },
  },
})
</script>

<style scoped>

</style>
